import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import InlineAlert from '../../../shared/components/InlineAlert';
import WorkflowViewer from './WorkflowViewer';
import WorkflowEditor from './WorkflowEditor';
import type { useWorkflowForm } from '../hooks/useWorkflowForm';

interface Props {
  form: ReturnType<typeof useWorkflowForm>;
}

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{children}</Box>
);

// The Studio's right-hand side for workflows: one version read-only, or the draft being edited.
const WorkflowSection: React.FC<Props> = ({ form }) => {
  if (form.mode === 'view') {
    return (
      <>
        {(form.actionError || form.notice) && (
          <Box sx={{ mx: 3, mt: 2 }}>
            {form.actionError ? (
              <InlineAlert severity="error" onClose={form.clearActionError}>
                {form.actionError}
              </InlineAlert>
            ) : (
              <InlineAlert severity="success">{form.notice}</InlineAlert>
            )}
          </Box>
        )}
        <WorkflowViewer
          workflow={form.selected}
          loading={form.detailLoading}
          versions={form.versions}
          publishing={form.publishing}
          deleting={form.deleting}
          onSelectVersion={(version) => {
            if (form.selectedRef) form.selectVersion(form.selectedRef.workflow_id, version);
          }}
          onEditDraft={form.editDraft}
          onPublish={form.publish}
          onDelete={form.deleteWorkflow}
        />
      </>
    );
  }

  // The editor seeds its working copy from the stored draft once, so it mounts only after the
  // draft has arrived; keyed by workflow, so opening another one starts a fresh copy.
  if (form.mode === 'edit' && !form.selected) {
    return (
      <Centered>
        {form.detailLoading ? (
          <CircularProgress />
        ) : (
          <Typography variant="body2" color="text.secondary">
            The draft could not be opened
          </Typography>
        )}
      </Centered>
    );
  }

  return (
    <WorkflowEditor
      key={form.mode === 'new' ? 'new' : form.selectedRef?.workflow_id}
      workflow={form.mode === 'new' ? null : form.selected}
      saving={form.saving}
      publishing={form.publishing}
      actionError={form.actionError}
      notice={form.notice}
      onDismissError={form.clearActionError}
      onSave={form.save}
      onPublish={form.publish}
      onCancel={form.cancelEdit}
      onDirtyChange={form.setDirty}
      discardPending={form.discardPending}
      onConfirmDiscard={form.confirmDiscard}
      onKeepEditing={form.keepEditing}
    />
  );
};

export default WorkflowSection;
